import React, { useState } from "react";
import {
  RefreshCw,
  Camera,
  FileText,
  ChevronRight,
  ChevronLeft,
  List,
  Shuffle,
  Pencil,
} from "lucide-react";
import { Button } from "@somethings-ui/web";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useHubStore } from "./use-hub-store";
import { cn } from "@/lib/utils";
import { usePostHogEvent } from "@/lib/posthog";
import { useFeatureFlagEnabled } from "@posthog/react";
import { useAbility } from "@/lib/casl/ability-context";

interface ActivityOption {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
  disabledReason?: string;
  onClick?: () => void;
}

interface AddActivityButtonProps {
  hasSwapInProgress?: boolean;
}

export function AddActivityButton({
  hasSwapInProgress = false,
}: AddActivityButtonProps) {
  const ability = useAbility();
  const canCreate = ability.can("create", "shareable-entities");

  const [open, setOpen] = useState(false);
  const [showSwapSubMenu, setShowSwapSubMenu] = useState(false);
  const navigateToView = useHubStore((state) => state.navigateToView);
  const setDraftNoteContent = useHubStore((state) => state.setDraftNoteContent);
  const setDraftSwap = useHubStore((state) => state.setDraftSwap);
  const { dispatchEvent } = usePostHogEvent("mentor_hub", { onMount: false });
  const isMomentsFlag = useFeatureFlagEnabled("enable-moments");
  const isSwapsFlag = useFeatureFlagEnabled("enable-swaps");
  const isMomentsEnabled = isMomentsFlag === true;
  const isSwapsEnabled = isSwapsFlag === true;
  const isSwapExtensionsFlag = useFeatureFlagEnabled("enable-swap-extensions");
  const isSwapExtensionsEnabled = isSwapExtensionsFlag === true;

  if (!canCreate) return null;

  const ACTIVITY_OPTIONS: ActivityOption[] = [
    {
      id: "swaps",
      label: "Swaps",
      icon: RefreshCw,
      disabled: !isSwapsEnabled || hasSwapInProgress,
      disabledReason: hasSwapInProgress ? "Swap in progress" : undefined,
      onClick:
        isSwapsEnabled && !hasSwapInProgress
          ? () => {
              if (isSwapExtensionsEnabled) {
                setShowSwapSubMenu(true);
              } else {
                setDraftSwap(null);
                navigateToView("create-swap");
              }
            }
          : undefined,
    },
    {
      id: "photos",
      label: "Moments",
      icon: Camera,
      disabled: !isMomentsEnabled,
      onClick: isMomentsEnabled
        ? () => {
            navigateToView("create-moment");
          }
        : undefined,
    },
    {
      id: "notes",
      label: "Notes",
      icon: FileText,
      onClick: () => {
        setDraftNoteContent(null);
        navigateToView("create-note");
      },
    },
  ];

  const SWAP_SUB_OPTIONS: ActivityOption[] = [
    {
      id: "pick-prompt",
      label: "Pick a prompt",
      icon: List,
      onClick: () => {
        setDraftSwap(null);
        navigateToView("create-swap");
      },
    },
    {
      id: "surprise-me",
      label: "Surprise me",
      icon: Shuffle,
      onClick: () => {
        setDraftSwap(null);
        navigateToView("surprise-swap");
      },
    },
    {
      id: "create-your-own",
      label: "Create your own",
      icon: Pencil,
      onClick: () => {
        setDraftSwap(null);
        navigateToView("custom-swap");
      },
    },
  ];

  const handleOptionClick = (option: ActivityOption) => {
    if (option.disabled) return;
    const opensSubMenu = option.id === "swaps" && isSwapExtensionsEnabled;
    if (!opensSubMenu) {
      dispatchEvent("mentor_click_add_activity");
    }
    option.onClick?.();
    if (!opensSubMenu) {
      setOpen(false);
      setShowSwapSubMenu(false);
    }
  };

  const handleSwapSubOptionClick = (option: ActivityOption) => {
    if (option.disabled) return;
    dispatchEvent("mentor_click_add_activity");
    option.onClick?.();
    setOpen(false);
    setShowSwapSubMenu(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setShowSwapSubMenu(false);
    }
  };

  const renderOptions = (
    options: ActivityOption[],
    onOptionClick: (option: ActivityOption) => void,
    hasHeader = false,
  ) => (
    <div className="divide-y divide-gray-200">
      {options.map((option, index) => {
        const Icon = option.icon;
        const isDisabled = option.disabled;
        return (
          <button
            key={option.id}
            onClick={() => onOptionClick(option)}
            disabled={isDisabled}
            className={cn(
              "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
              "hover:bg-gray-50 disabled:cursor-not-allowed",
              index === 0 && !hasHeader && "rounded-t-2xl",
              index === options.length - 1 && "rounded-b-2xl",
            )}
          >
            <span className={cn(isDisabled ? "text-gray-400" : "text-black")}>
              <Icon className="h-5 w-5" />
            </span>
            <span
              className={cn(
                "flex-1 font-medium",
                isDisabled ? "text-gray-400" : "text-black",
              )}
            >
              {option.label}
              {option.disabledReason && (
                <span className="ml-1 text-gray-300">
                  ({option.disabledReason})
                </span>
              )}
            </span>
            {option.id === "swaps" && isSwapExtensionsEnabled && (
              <ChevronRight
                className={cn(
                  "h-4 w-4",
                  isDisabled ? "text-gray-300" : "text-gray-400",
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="relative px-4 py-4">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            className="w-full rounded-full border border-black bg-white font-semibold text-black hover:bg-gray-50"
          >
            Add activity
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="center"
          sideOffset={8}
          className={cn(
            "w-[var(--radix-popover-trigger-width)] rounded-2xl p-0 shadow-lg",
          )}
        >
          {showSwapSubMenu ? (
            <>
              <button
                onClick={() => setShowSwapSubMenu(false)}
                className="flex w-full items-center gap-2 rounded-t-2xl border-b border-gray-200 px-4 py-2.5 text-left text-sm text-gray-500 transition-colors hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
              {renderOptions(SWAP_SUB_OPTIONS, handleSwapSubOptionClick, true)}
            </>
          ) : (
            renderOptions(ACTIVITY_OPTIONS, handleOptionClick)
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
